<script lang="ts" setup>
import { ref, onMounted, computed } from "vue";
import { Button, Menu, Textarea } from "primevue";
import { useAuthStore } from "@/stores/auth.store";
import { useThreadStore } from "@/stores/thread.store";
import { Configuration, ThreadsApi, type Message } from "@/api/javaapi";
import axios from "axios";
import { useMessageTemplatesStore } from "@/stores/message-templates.store";
import { useToast } from "primevue/usetoast";
import type { MenuItem } from "primevue/menuitem";
import FileUpload from "primevue/fileupload";

type EnhancedFileUpload = InstanceType<typeof FileUpload> & {
  choose: () => void;
  clear: () => void;
  files: File[];
};

const { threadId } = defineProps<{ threadId: number }>();
const emit = defineEmits<{
  (e: "send-message", message: Message): void;
  (e: "send-message-success", data: { tempId: number, message: Message }): void;
  (e: "send-message-failed", message: Message): void;
  (e: "update-message", message: Message): void;
}>();

const authStore = useAuthStore();
const threadStore = useThreadStore();
const messageTemplatesStore = useMessageTemplatesStore();
const toast = useToast();

const fileUploader = ref<EnhancedFileUpload | null>(null);
const content = ref<string>("");
const menu = ref<InstanceType<typeof Menu> | null>(null);
const loading = ref(false);

const threadApi = new ThreadsApi(new Configuration({ accessToken: authStore.token ?? undefined }));

onMounted(() => {
  messageTemplatesStore.fetchTemplates();
});

// Reusable function to apply templates
const applyTemplate = async (template: any) => {
  const reservationId = threadStore.getOpenThread(threadId)?.reservation;
  if (reservationId) {
    const processedTemplate = await messageTemplatesStore.getProcessedTemplate(template.id!, reservationId);
    content.value = processedTemplate?.content || "";
  } else {
    content.value = template.content || "";
  }
};

const menuItems = computed((): MenuItem[] => {
  const items: MenuItem[] = [
    {
      label: "Attach File",
      icon: "pi pi-paperclip",
      command: () => fileUploader.value?.choose(),
    },
  ];

  const textTemplates = messageTemplatesStore.templates.filter(t => t.templateType === "Text");
  const channelTemplates = messageTemplatesStore.templates.filter(t => t.templateType === "Channel");

  if (textTemplates.length > 0) {
    items.push({
      label: "Text Templates",
      icon: "pi pi-file",
      items: textTemplates.map(template => ({
        label: template.name || "Untitled Text Template",
        command: () => applyTemplate(template),
      })),
    });
  }

  if (channelTemplates.length > 0) {
    items.push({
      label: "Channel Templates",
      icon: "pi pi-comments",
      items: channelTemplates.map(template => ({
        label: template.name || "Untitled Channel Template",
        command: () => applyTemplate(template),
      })),
    });
  }

  return items;
});

const hasFiles = computed(() => (fileUploader.value?.files?.length || 0) > 0);
const isSendDisabled = computed(() => !content.value.trim() && !hasFiles.value);

const sendMessage = async () => {
  if (isNaN(Number(threadId))) {
    console.error("Invalid threadId");
    toast.add({ severity: "error", summary: "Error", detail: "Invalid thread ID", life: 3000 });
    return;
  }

  loading.value = true;

  if (!authStore.token) {
    console.error("No auth token available");
    toast.add({ severity: "error", summary: "Error", detail: "No auth token available", life: 3000 });
    loading.value = false;
    return;
  }

  const tempMessage: Message = {
    content: content.value,
    threadID: Number(threadId),
    attachmentIDs: [],
    sendStatus: "PENDING",
    timestamp: new Date().getTime(),
    id: Date.now(),
    sender: true,
  };

  emit("send-message", tempMessage);

  try {
    if (fileUploader.value?.files?.length) {
      const attachmentIds = await Promise.all(
        fileUploader.value.files.map(async (file: File) => {
          const formData = new FormData();
          formData.append("file", file, file.name);
          formData.append("type", file.type);
          formData.append("name", file.name);

          const fileResponse = await axios.post("/brightside/brightside/upload", formData);
          return fileResponse.data as number;
        })
      );
      tempMessage.attachmentIDs = attachmentIds;
    }

    const newMessage: Message = {
      content: content.value,
      threadID: Number(threadId),
      attachmentIDs: tempMessage.attachmentIDs,
    };

    const response = await threadApi.sendMessage(threadId, newMessage);
    newMessage.id = response.data;

    emit("send-message-success", {
      tempId: tempMessage.id ?? 0,
      message: {
        ...newMessage,
        sendStatus: "SENT",
        timestamp: new Date().getTime(),
        sender: true,
      }
    });
  } catch (error) {
    console.error("Failed to send message:", error);
    toast.add({ severity: "error", summary: "Error", detail: "Failed to send message", life: 3000 });
    emit("send-message-failed", {
      ...tempMessage,
      sendStatus: "FAILED",
    });
  } finally {
    loading.value = false;
    content.value = "";
    fileUploader.value?.clear();
  }
};

const retryMessage = async (failedMessage: Message) => {
  loading.value = true;

  emit("update-message", {
    ...failedMessage,
    sendStatus: "PENDING",
  });

  try {
    const newMessage: Message = {
      content: failedMessage.content,
      threadID: Number(threadId),
      attachmentIDs: failedMessage.attachmentIDs || [],
    };

    const response = await threadApi.sendMessage(threadId, newMessage);
    newMessage.id = response.data;

    emit("send-message-success", {
      tempId: failedMessage.id ?? 0,
      message: {
        ...newMessage,
        sendStatus: "SENT",
        timestamp: new Date().getTime(),
        sender: true,
      }
    });
  } catch (error) {
    console.error("Retry failed:", error);
    toast.add({ severity: "error", summary: "Error", detail: "Failed to retry message", life: 3000 });
    emit("update-message", {
      ...failedMessage,
      sendStatus: "FAILED",
    });
  } finally {
    loading.value = false;
    content.value = "";
  }
};

defineExpose({ retryMessage });
</script>

<template>
  <form
    class="flex items-end gap-1 p-2 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 overflow-y-auto"
    @submit.prevent="sendMessage"
  >
    <Button
      icon="pi pi-plus"
      class="p-button-rounded p-button-text p-button-sm"
      @click="menu?.toggle($event)"
      aria-haspopup="true"
      aria-controls="overlay_menu"
    />
    <div class="flex-1 max-h-[200px] overflow-y-auto">
      <FileUpload
        v-show="hasFiles"
        ref="fileUploader"
        mode="advanced"
        name="file[]"
        accept="image/*"
        :maxFileSize="1000000"
        :multiple="true"
        :customUpload="true"
        :auto="false"
        :showUploadButton="false"
        :showCancelButton="false"
        :disabled="loading"
        class="mb-2 max-h-24 overflow-y-auto"
      />
      <Textarea
        v-model="content"
        autoResize
        rows="1"
        class="w-full text-sm rounded-2xl align-bottom"
        placeholder="Type a message..."
        @keydown.enter.exact.prevent="sendMessage"
      />
    </div>
    <Button
      type="submit"
      label="Send"
      class="p-button-sm p-button-primary ml-2 rounded-2xl"
      :disabled="isSendDisabled"
      :loading="loading"
    />
    <Menu ref="menu" id="overlay_menu" :model="menuItems" :popup="true" class="w-48" />
  </form>
</template>

<style scoped>
:deep(.p-fileupload) {
  border: none;
  --p-fileupload-file-padding: 0.25rem;
  --p-fileupload-file-info-gap: 0;
}

:deep(.p-fileupload .p-fileupload-content) {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0;
  border: none;
  background: transparent;
}

:deep(.p-fileupload .p-fileupload-header),
:deep(.p-fileupload .p-fileupload-row) {
  display: none;
}

:deep(.p-fileupload .p-fileupload-files .p-fileupload-file) {
  margin: 0;
  padding: 0.1rem;
}
</style>